import { AuthProvider, LinkConflictData } from "./dto/auth.dto.js";
import type { PlayerDto } from "./dto/player.dto.js";
import { ApiError, NetworkError } from "./http_client.js";
import { connectApple, connectFacebook, connectGoogle } from "./requests/auth.requests.js";
import { AuthSessionStore } from "./auth_session.js";
import { syncPlayerAfterLogin, syncProfileFromServer } from "./player_sync.js";
import { getPlayer } from "./requests/player.requests.js";
import { OfflineSyncService } from "../offline/offline_sync_service.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { PlayerSyncService } from "./player_sync_service.js";
import { LeaderboardSyncService } from "./leaderboard/leaderboard_sync_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { AvatarUtils } from "../common/avatar_utils.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { ConfigSyncService } from "../config_sync/config_sync_service.js";
import { GoogleSignInPlugin } from "../plugins/play_games_plugin.js";
import { FacebookSignInPlugin } from "../plugins/facebook_sign_in_plugin.js";
import { AppleSignInPlugin } from "../plugins/apple_sign_in_plugin.js";
import { AppleWebSignIn } from "../plugins/apple_web_sign_in.js";
import { updatePlayer } from "./requests/player.requests.js";
import type { UpdatePlayerRequest } from "./dto/player.dto.js";
import { clearFacebookAvatarCache } from "../common/facebook_avatar_loader.js";
import { readStoredAvatar } from "../common/avatar_image_binder.js";
import {
  LinkedSocialProvider,
  initLinkedProvidersFromServer,
  resetLinkedSocialProviders,
} from "../ui/dialog/account_providers_preview_state.js";

export type LinkResolve = 'keep_existing' | 'keep_current';
export type LinkErrorReason = 'unavailable' | 'disabled' | 'network' | 'generic';

export type LinkOutcome =
  | { kind: 'linked'; player: PlayerDto }
  | { kind: 'conflict'; conflict: LinkConflictData; resolve: ( choice: LinkResolve ) => Promise<LinkOutcome> }
  | { kind: 'cancelled' }
  | { kind: 'error'; reason: LinkErrorReason; error: unknown };

interface ProviderCredential
{
  token: string;
  provider_id: string;
}

const CANCEL_CODES = new Set( [ '12501', '1001', 'cancelled' ] );

export function isNativePlatform(): boolean
{
  const platform = ( window as any ).Capacitor?.getPlatform?.();
  return platform === 'ios' || platform === 'android';
}

export function applePlugin(): typeof AppleSignInPlugin | typeof AppleWebSignIn
{
  return isNativePlatform() ? AppleSignInPlugin : AppleWebSignIn;
}

export function isProviderAvailable( provider: LinkedSocialProvider ): boolean
{
  switch ( provider )
  {
    case LinkedSocialProvider.Google:   return GoogleSignInPlugin.isAvailable();
    case LinkedSocialProvider.Facebook: return FacebookSignInPlugin.isAvailable();
    case LinkedSocialProvider.Apple:    return applePlugin().isAvailable();
  }
}

export async function clearFacebookAvatar(): Promise<void>
{
  const had_facebook_avatar = readStoredAvatar().avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID;
  localStorage.removeItem( 'player_facebook_avatar_url' );
  clearFacebookAvatarCache();
  if ( had_facebook_avatar )
    localStorage.setItem( 'player_avatar_id', '0' );

  const patch: UpdatePlayerRequest = { facebook_avatar_url: null };
  if ( had_facebook_avatar ) patch.avatar_id = 0;
  await updatePlayer( patch ).catch( e => console.warn( '[AccountLink] clear facebook avatar failed', e ) );

  const name = localStorage.getItem( 'player_nickname' ) ?? 'Player';
  void LeaderboardSyncService.syncProfile( StatisticsStorage.load().level, name, readStoredAvatar().avatar_id );
}

export function currentAccountProvider( player: PlayerDto ): AuthProvider
{
  const linked = ( player.linked_providers ?? [] ).map( p => p.provider );
  for ( const candidate of [ AuthProvider.Google, AuthProvider.Facebook, AuthProvider.Apple ] )
  {
    if ( linked.includes( candidate ) ) return candidate;
  }
  return AuthProvider.Guest;
}

export function isSignInCancelled( e: unknown ): boolean
{
  const code = String( ( e as any )?.code ?? '' ).toLowerCase();
  const message = String( ( e as any )?.message ?? e ?? '' ).toLowerCase();
  if ( CANCEL_CODES.has( code ) ) return true;
  return /closed by user|cancel/.test( message ) || /\b12501\b/.test( message );
}

function isSignInUnavailable( e: unknown ): boolean
{
  const code = String( ( e as any )?.code ?? '' );
  const message = String( ( e as any )?.message ?? '' );
  return code === 'missing_client_id' || /missing_client_id|popup blocked/i.test( message );
}

export function classifyLinkError( e: unknown ): LinkErrorReason
{
  if ( e instanceof NetworkError ) return 'network';
  if ( e instanceof ApiError && e.status === 503 ) return 'disabled';
  return 'generic';
}

function decodeJwtSubject( token: string ): string
{
  try
  {
    const payload = JSON.parse( atob( token.split( '.' )[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' ) ) );
    return typeof payload.sub === 'string' ? payload.sub : '';
  }
  catch
  {
    return '';
  }
}

async function obtainCredential( provider: LinkedSocialProvider ): Promise<ProviderCredential>
{
  switch ( provider )
  {
    case LinkedSocialProvider.Google:
    {
      await GoogleSignInPlugin.signOut();
      const { idToken } = await GoogleSignInPlugin.signIn();
      if ( !idToken ) throw Object.assign( new Error( 'missing_id_token' ), { code: 'missing_client_id' } );
      return { token: idToken, provider_id: decodeJwtSubject( idToken ) };
    }
    case LinkedSocialProvider.Facebook:
    {
      await FacebookSignInPlugin.signOut();
      const { accessToken, userId } = await FacebookSignInPlugin.signIn();
      return { token: accessToken, provider_id: userId ?? '' };
    }
    case LinkedSocialProvider.Apple:
    {
      const { identityToken, userId } = await applePlugin().signIn();
      return { token: identityToken, provider_id: userId || decodeJwtSubject( identityToken ) };
    }
  }
}

function cacheProviderId( provider: LinkedSocialProvider, provider_id: string ): void
{
  if ( !provider_id ) return;
  switch ( provider )
  {
    case LinkedSocialProvider.Google:   GoogleSignInPlugin.setCachedId( provider_id ); break;
    case LinkedSocialProvider.Facebook: FacebookSignInPlugin.setCachedId( provider_id ); break;
    case LinkedSocialProvider.Apple:    applePlugin().setCachedId( provider_id ); break;
  }
}

function connect( provider: LinkedSocialProvider, token: string, resolve?: LinkResolve ): Promise<PlayerDto>
{
  switch ( provider )
  {
    case LinkedSocialProvider.Google:   return connectGoogle( token, resolve );
    case LinkedSocialProvider.Facebook: return connectFacebook( token, resolve );
    case LinkedSocialProvider.Apple:    return connectApple( token, resolve );
  }
}

function syncFacebookAvatarToLeaderboard( player: PlayerDto ): void
{
  const fb_url = player.facebook_avatar_url ?? undefined;
  if ( !fb_url ) return;
  const name = player.nickname ?? localStorage.getItem( 'player_nickname' ) ?? 'Player';
  void LeaderboardSyncService.syncProfile( player.player_level ?? 1, name, AvatarUtils.FACEBOOK_AVATAR_ID, fb_url );
}

export async function applyLinkedPlayer( player: PlayerDto, provider: LinkedSocialProvider, provider_id: string ): Promise<void>
{
  const switched_account = AuthSessionStore.getPlayerId() !== player.id;
  AuthSessionStore.applyPlayer( player );
  if ( switched_account ) OfflineJournal.clear();

  cacheProviderId( provider, provider_id );
  resetLinkedSocialProviders();
  initLinkedProvidersFromServer( player.linked_providers ?? [] );

  let synced_player = player;
  if ( !switched_account && OfflineJournal.hasPending() && !OfflineSyncService.isReplaying() )
  {
    const stats = await OfflineSyncService.replayPending();
    if ( stats.replayed > 0 )
      synced_player = { ...( await getPlayer() ), linked_providers: player.linked_providers };
  }
  if ( OfflineJournal.hasPending() )
  {
    console.warn( '[AccountLink] offline journal still pending, keeping local progress until the next sync' );
    syncProfileFromServer( synced_player );
    OfflineEconomy.restoreInventoryFromStats();
  }
  else
    await syncPlayerAfterLogin( synced_player );
  StatisticsStorage.onUpdateHook = ( partial ) => { void PlayerSyncService.onStatUpdate( partial ); };

  if ( provider === LinkedSocialProvider.Facebook ) syncFacebookAvatarToLeaderboard( player );
  if ( switched_account ) void ConfigSyncService.refreshAll( 'account_switch' );

  console.log( `[AccountLink] linked ${ LinkedSocialProvider[ provider ] }, player ${ player.id }${ switched_account ? ' (switched account)' : '' }` );
}

async function connectWith( provider: LinkedSocialProvider, credential: ProviderCredential, resolve?: LinkResolve ): Promise<LinkOutcome>
{
  try
  {
    const player = await connect( provider, credential.token, resolve );
    await applyLinkedPlayer( player, provider, credential.provider_id );
    return { kind: 'linked', player };
  }
  catch ( e )
  {
    if ( e instanceof ApiError && e.status === 409 && !resolve )
    {
      return {
        kind: 'conflict',
        conflict: e.body as LinkConflictData,
        resolve: ( choice ) => connectWith( provider, credential, choice ),
      };
    }
    console.warn( `[AccountLink] connect ${ LinkedSocialProvider[ provider ] } failed:`, e );
    return { kind: 'error', reason: classifyLinkError( e ), error: e };
  }
}

export async function linkProvider( provider: LinkedSocialProvider ): Promise<LinkOutcome>
{
  if ( !isProviderAvailable( provider ) )
    return { kind: 'error', reason: 'unavailable', error: new Error( 'provider unavailable' ) };

  let credential: ProviderCredential;
  try
  {
    credential = await obtainCredential( provider );
  }
  catch ( e )
  {
    if ( isSignInCancelled( e ) ) return { kind: 'cancelled' };
    console.warn( `[AccountLink] ${ LinkedSocialProvider[ provider ] } sign-in failed:`, e );
    return { kind: 'error', reason: isSignInUnavailable( e ) ? 'unavailable' : 'generic', error: e };
  }

  return connectWith( provider, credential );
}
